import { CostPriceAnalysis } from "../../../../services/costPrice/costPriceService";
import { RevenueHistory } from "../../../../services/revenuehistory/revenueHistoryService";
import { CostPriceCategory } from "../../../../services/costPrice/costPriceCategoriesService";
import { DishRowData } from "./costPriceDataTypes";
import {
    getEffectiveMenuPrice,
    calculateCostPercentage,
    getQuantitySoldLast14Days,
    COST_PRICE_TYPE_CHOICE,
} from "../../../../utils/costPriceUtils";
import { getRevenueVenueLabel, getRevenueItemDisplayName } from "../../../../utils/revenueHistoryCalculations";

/**
 * Calculate average quantity sold across all items for comparison
 */
function calculateAveragePopularity(
    revenueHistory: RevenueHistory[],
    revenueItems: string[]
): number {
    if (revenueItems.length === 0) return 0;

    const totalQuantity = revenueItems.reduce((sum, itemName) => {
        return sum + getQuantitySoldLast14Days(itemName, revenueHistory);
    }, 0);

    return totalQuantity / revenueItems.length;
}

/**
 * Transform CostPriceAnalysis to DishRowData for the UI table
 */
export function transformToDishRowData(
    analysis: CostPriceAnalysis,
    revenueHistory: RevenueHistory[],
    categories: CostPriceCategory[],
    showExVat: boolean = true,
    averagePopularity: number = 0,
    revenueSourceType?: string,
    venueLabel?: string
): DishRowData {
    // Find category
    const category = categories.find(cat => cat.id === analysis.categoryId);
    const categoryName = category?.name || "Uncategorized";
    const categoryColor = category?.color || "#EBF8FF";
    const isDefaultCategory = category?.isDefault || false;

    // Get menu price
    const effectivePrice = getEffectiveMenuPrice(analysis, revenueHistory);
    const menuPrice = effectivePrice
        ? (showExVat ? effectivePrice.menuExVatPrice : effectivePrice.menuPrice)
        : 0;
    const menuPriceExVat = effectivePrice?.menuExVatPrice || 0;

    // Calculate cost percentage
    const costPct = calculateCostPercentage(analysis.totalCost, menuPrice);

    // Calculate margin
    const margin = menuPrice - analysis.totalCost;
    const marginPct = menuPrice > 0 ? (margin / menuPrice) * 100 : 0;

    // Get popularity (quantity sold in last 2 months)
    const popularity = analysis.menuItemName
        ? getQuantitySoldLast14Days(analysis.menuItemName, revenueHistory)
        : 0;

    // Determine if popularity is above or below average for the trending indicator
    // The actual value stored is the quantity, but we compare it to average for display
    const isPopularityHigh = averagePopularity > 0 ? popularity >= averagePopularity : popularity > 0;

    // Matrix classification will be calculated by menuEngineeringService after transformation
    const matrix: "Star" | "Dog" | "Puzzle" | "Plowhorse" | null = null;

    // Check if this is a placeholder (revenue item without cost price analysis created)
    const isPlaceholder = analysis.id.startsWith('placeholder-');

    // Check if this is an uncategorized placeholder (placeholder-id and no ingredients/cost)
    // These are the true "uncategorized" items that should have reduced opacity
    const isUncategorizedPlaceholder = isPlaceholder;

    return {
        ...analysis,
        dishName: analysis.costPriceName,
        venueLabel,
        displayName: getRevenueItemDisplayName(analysis.costPriceName, venueLabel),
        category: categoryName,
        categoryColor,
        isDefaultCategory,
        matrix,
        margin,
        marginPercentage: marginPct,
        costPercentage: costPct,
        popularity,
        isPopularityHigh,
        price: menuPrice,
        priceExVat: menuPriceExVat,
        isPlaceholder,
        isUncategorizedPlaceholder,
        revenueSourceType
    };
}

/**
 * Transform multiple analyses and create placeholders for revenue items without cost analysis
 */
export function transformCostPriceListToDishData(
    costPriceList: CostPriceAnalysis[],
    revenueHistory: RevenueHistory[],
    revenueItems: string[],
    categories: CostPriceCategory[],
    showExVat: boolean = true,
    restaurantId?: string
): DishRowData[] {
    const result: DishRowData[] = [];
    const averagePopularity = calculateAveragePopularity(revenueHistory, revenueItems);

    // Build a map of revenue items to their source type, plus the partner venue each name
    // belongs to. A partner venue (bar POP) sells the restaurant's OWN dishes under the same
    // names, so a name is only labelled with the venue when EVERY occurrence of it is that
    // venue's — a shared name is one dish with one cost price and must stay unlabelled.
    const revenueItemTypeMap = new Map<string, string>();
    const venueSeenByItem = new Map<string, { venues: Set<string>; hasOwn: boolean }>();
    revenueHistory.forEach(revenue => {
        if (revenue.revenueData?.categories) {
            revenue.revenueData.categories.forEach(category => {
                const venueLabel = getRevenueVenueLabel(category.name);
                category.items.forEach(item => {
                    if (!item.name) return;
                    if (!revenueItemTypeMap.has(item.name)) {
                        revenueItemTypeMap.set(item.name, revenue.revenueSourceType || '');
                    }
                    let seen = venueSeenByItem.get(item.name);
                    if (!seen) {
                        seen = { venues: new Set<string>(), hasOwn: false };
                        venueSeenByItem.set(item.name, seen);
                    }
                    if (venueLabel) seen.venues.add(venueLabel);
                    else seen.hasOwn = true;
                });
            });
        }
    });

    const venueLabelFor = (itemName: string): string | undefined => {
        const seen = venueSeenByItem.get(itemName);
        if (!seen || seen.hasOwn || seen.venues.size !== 1) return undefined;
        return seen.venues.values().next().value;
    };

    // Track which analyses have been added
    const addedAnalysisIds = new Set<string>();

    // Process each revenue item first
    revenueItems.forEach(itemName => {
        const existingAnalysis = costPriceList.find(
            // CHOICE rows are shared-by-name CHOICE analyses; they should never
            // back a revenue-item row (they have no menuItemName anyway, but be
            // defensive against bad data).
            analysis => analysis.menuItemName === itemName
                && analysis.costPriceType !== COST_PRICE_TYPE_CHOICE
        );

        const revenueSourceType = revenueItemTypeMap.get(itemName);
        const venueLabel = venueLabelFor(itemName);

        if (existingAnalysis) {
            // Item has cost price analysis
            const dishData = transformToDishRowData(
                existingAnalysis,
                revenueHistory,
                categories,
                showExVat,
                averagePopularity,
                revenueSourceType,
                venueLabel
            );
            result.push(dishData);
            addedAnalysisIds.add(existingAnalysis.id);
        } else {
            // Create placeholder
            const defaultCategory = categories.find(c => c.isDefault);
            const placeholder: CostPriceAnalysis = {
                id: `placeholder-${itemName}`,
                restaurantId: restaurantId || '',
                costPriceName: itemName,
                menuItemName: itemName,
                categoryId: defaultCategory?.id,
                ingredients: [],
                totalCost: 0,
                isCustomItem: false,
                createdAt: new Date(),
                updatedAt: new Date()
            };

            const dishData = transformToDishRowData(
                placeholder,
                revenueHistory,
                categories,
                showExVat,
                averagePopularity,
                revenueSourceType,
                venueLabel
            );
            result.push(dishData);
        }
    });

    // Add custom items (items not from revenue data). CHOICE rows are emitted
    // separately below — they are NOT custom-items and must not appear in
    // any of the food/drinks/all tabs.
    costPriceList.forEach(analysis => {
        if (analysis.costPriceType === COST_PRICE_TYPE_CHOICE) return;
        if (analysis.isCustomItem && !addedAnalysisIds.has(analysis.id)) {
            // For unlinked items, use costPriceType as the revenueSourceType so they appear in the correct tab
            const typeForTab = !analysis.menuItemName && analysis.costPriceType
                ? analysis.costPriceType
                : undefined;
            const dishData = transformToDishRowData(
                analysis,
                revenueHistory,
                categories,
                showExVat,
                averagePopularity,
                typeForTab
            );
            result.push(dishData);
            addedAnalysisIds.add(analysis.id);
        }
    });

    // Emit one row per CHOICE analysis (shared per-pick cost prices used by
    // BYO dishes). These rows live ONLY on the Add-ons tab — the page
    // filters them in/out based on `activeTab`. We tag them with
    // `isChoiceCost: true` so DishTable can render the trimmed layout
    // (name + per-pick cost; price / margin / popularity / matrix = "--").
    costPriceList.forEach(analysis => {
        if (analysis.costPriceType !== COST_PRICE_TYPE_CHOICE) return;
        if (addedAnalysisIds.has(analysis.id)) return;
        const dishData: DishRowData = {
            ...transformToDishRowData(
                analysis,
                revenueHistory,
                categories,
                showExVat,
                averagePopularity,
                undefined // CHOICE rows don't carry a revenueSourceType — they're system-managed
            ),
            // Force the columns that don't apply to a CHOICE row.
            price: 0,
            priceExVat: 0,
            margin: 0,
            marginPercentage: 0,
            costPercentage: 0,
            popularity: 0,
            matrix: null,
            isChoiceCost: true,
        };
        result.push(dishData);
        addedAnalysisIds.add(analysis.id);
    });

    return result;
}
